/**
 * 我的 · 成员档案。
 *
 * 与原型的关键差别：
 *   - 四大共建圈层的连接状态来自后端（原型恒显示"已连接"，是纯装饰）；
 *   - 档案可编辑并落库（PATCH /api/member/profile），改完首页问候语与成员卡同步更新；
 *   - 退出登录会吊销服务端会话，且带二次确认（原型是点一下直接跳登录页）。
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Member, MemberRing } from '@hew3/shared';
import { complianceText } from '@hew3/shared';
import { api } from '../../lib/api.ts';
import { formatDate } from '../../lib/format.ts';
import { useAuth } from '../../store/auth.ts';
import { useUi } from '../../store/ui.ts';
import { Avatar, Badge, Button, Card, ListItem, StateBox } from '../../components/ui.tsx';

interface ProfileResponse {
  member: Member;
  rings: MemberRing[];
}

interface EditForm {
  fullName: string;
  role: string;
  company: string;
  city: string;
}

export function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);
  const refreshMe = useAuth((s) => s.refreshMe);
  const logout = useAuth((s) => s.logout);

  const profileQuery = useQuery({
    queryKey: ['member', 'profile'],
    queryFn: () => api.get<ProfileResponse>('/api/member/profile'),
  });

  const member = profileQuery.data?.member;
  const [form, setForm] = useState<EditForm>({ fullName: '', role: '', company: '', city: '' });
  const [syncedFor, setSyncedFor] = useState<string | null>(null);
  const [confirmLogout, setConfirmLogout] = useState(false);

  // 表单初值只用一次：避免用户正在输入时被后台刷新覆盖
  useEffect(() => {
    if (!member || syncedFor === member.id) return;
    setSyncedFor(member.id);
    setForm({
      fullName: member.fullName,
      role: member.role,
      company: member.company,
      city: member.city,
    });
  }, [member, syncedFor]);

  const saveProfile = useMutation({
    mutationFn: (payload: EditForm) =>
      api.patch<{ member: Member }>('/api/member/profile', {
        fullName: payload.fullName.trim(),
        role: payload.role.trim(),
        company: payload.company.trim(),
        city: payload.city.trim(),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData<ProfileResponse>(['member', 'profile'], (prev) =>
        prev ? { ...prev, member: data.member } : { member: data.member, rings: [] },
      );
      void queryClient.invalidateQueries({ queryKey: ['member', 'profile'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['auth'] });
      void refreshMe();
      showToast('档案已更新');
    },
    onError: () => {
      showToast('档案保存失败，请检查输入后重试', 'error');
    },
  });

  const doLogout = useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      showToast('已退出登录，服务端会话已吊销');
    },
  });

  if (profileQuery.isLoading) {
    return (
      <div className="page-pad">
        <div className="skeleton skel-card" style={{ height: 168 }} />
        <div className="skeleton skel-card" style={{ height: 140 }} />
        <div className="skeleton skel-card" />
      </div>
    );
  }

  if (profileQuery.isError || !profileQuery.data) {
    return (
      <StateBox
        kind="error"
        title="成员档案加载失败"
        desc="请检查网络后重试。"
        action={<Button small onClick={() => void profileQuery.refetch()}>重新加载</Button>}
      />
    );
  }

  const { rings } = profileQuery.data;
  const me = profileQuery.data.member;
  const dirty =
    form.fullName.trim() !== me.fullName ||
    form.role.trim() !== me.role ||
    form.company.trim() !== me.company ||
    form.city.trim() !== me.city;
  const canSubmit = dirty && form.fullName.trim().length > 0 && !saveProfile.isPending;

  return (
    <div className="page">
      {/* ---------------- 成员身份卡 ---------------- */}
      <div className="member-card">
        <div className="mc-lv">{me.levelEn}</div>
        <div className="mc-name">{me.fullName}</div>
        <div className="mc-no">{me.memberNo}</div>
        <div className="mc-row">
          <div>
            <div className="mc-k" style={{ fontSize: 11 }}>守护时长</div>
            <div className="mc-v">{me.guardMonths} 个月</div>
          </div>
          <div>
            <div className="mc-k" style={{ fontSize: 11 }}>入会日期</div>
            <div className="mc-v">{formatDate(me.joinDate)}</div>
          </div>
          <div>
            <div className="mc-k" style={{ fontSize: 11 }}>所在城市</div>
            <div className="mc-v">{me.city}</div>
          </div>
        </div>
        <div className="mc-row">
          <div>
            <div className="mc-k" style={{ fontSize: 11 }}>成员等级</div>
            <div className="mc-v">{me.levelName}</div>
          </div>
          <div>
            <div className="mc-k" style={{ fontSize: 11 }}>推荐人</div>
            <div className="mc-v">{me.referrer}</div>
          </div>
        </div>
        <div className="sec-note" style={{ marginTop: 14 }}>
          守护时长由入会日期与当前日期实时计算，不是固定文案。
        </div>
      </div>

      {/* ---------------- 四大共建圈层 ---------------- */}
      <Card title="四大共建圈层" more={<span className="card-more">真实连接状态</span>}>
        {rings.length === 0 ? (
          <StateBox title="暂无圈层记录" desc="共建圈层会随服务开通逐步接入" />
        ) : (
          rings.map((ring) => (
            <div key={ring.id} className="ring-item">
              <div
                className="ring-badge"
                style={{ background: 'rgba(255,255,255,.055)', border: '1px solid var(--line)' }}
                aria-hidden
              >
                {ring.icon}
              </div>
              <div className="grow">
                <div className="li-t">{ring.name}</div>
                <div className="li-s">{ring.desc}</div>
              </div>
              <Badge tone={ring.connected ? 'ok' : 'ghost'}>
                <span style={{ fontSize: 11 }}>{ring.connected ? '已连接' : '未连接'}</span>
              </Badge>
            </div>
          ))
        )}
      </Card>

      {/* ---------------- 我的服务与记录 ---------------- */}
      <Card title="我的服务与记录">
        <ListItem
          icon={<span style={{ fontSize: 16 }}>🔒</span>}
          title="隐私与授权"
          sub="四级数据授权逐项开关，可随时撤回"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/profile/consent')}
        />
        <ListItem
          icon={<span style={{ fontSize: 16 }}>🏥</span>}
          title="服务方授权"
          sub="逐家机构、逐个数据域授权，并可查看服务方读过我的什么数据"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/profile/provider-consent')}
        />
        <ListItem
          icon={<span style={{ fontSize: 16 }}>🧾</span>}
          title="数据调用记录"
          sub="谁在什么时候、因何目的调用了哪一域数据"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/profile/audit')}
        />
        <ListItem
          icon={<span style={{ fontSize: 16 }}>📜</span>}
          title="法务文本与同意记录"
          sub="隐私政策、用户协议、知情同意书等，含我的确认历史"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/profile/policies')}
        />
        <div className="sec-note">
          想了解 AI 如何使用这些数据，可前往
          <Link to="/ai"> AI 三螺旋管家</Link>；每次回答都会列出归因链与本次调用的数据域。
        </div>
      </Card>

      {/* ---------------- 三康与原有页面入口 ---------------- */}
      {/* 新版信息架构把底部导航收敛为 叁 / 三康 / 报告 / 服务 / 我的；
          未进入底部导航的页面（三螺旋总览、生命健康、企业治理、财富健康、传承）
          全部保留并从这里可达，避免既有能力被导航改版「藏起来」。 */}
      <Card title="三康与相关页面" more={<span className="card-more">全部原有页面保留</span>}>
        <ListItem
          icon={<span className="hl-h">叁</span>}
          title="三康综合指数"
          sub="H / E / W 的加权几何平均、螺旋状态、传导通道与干预杠杆排序"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/sk')}
        />
        <ListItem
          icon={<span>◍</span>}
          title="三螺旋总览"
          sub="健康—企业—财富作为一个整体的总览视图"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/home')}
        />
        <ListItem
          icon={<span className="hl-h">H</span>}
          title="生命健康"
          sub="H-Score™ 健康资本评分与六层指标体系"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/health')}
        />
        <ListItem
          icon={<span className="hl-e">E</span>}
          title="企业治理健康"
          sub="企业健康度、关键人与健康资产负债表"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/enterprise')}
        />
        <ListItem
          icon={<span className="hl-w">W</span>}
          title="财富健康"
          sub="健康信托方案与财富参数"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/wealth')}
        />
        <ListItem
          icon={<span>◍</span>}
          title="传承学院"
          sub="三层五维培养矩阵"
          right={<span aria-hidden>›</span>}
          onClick={() => navigate('/succession')}
        />
      </Card>

      {/* ---------------- 档案编辑 ---------------- */}
      <Card title="档案编辑" more={<span className="card-more">修改后立即生效</span>}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            saveProfile.mutate(form);
          }}
        >
          <div className="field">
            <label className="field-label" htmlFor="pf-name">姓名</label>
            <input
              id="pf-name"
              className="field-input"
              value={form.fullName}
              maxLength={40}
              onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="pf-role">职位</label>
            <input
              id="pf-role"
              className="field-input"
              value={form.role}
              maxLength={80}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="pf-company">企业名称</label>
            <input
              id="pf-company"
              className="field-input"
              value={form.company}
              maxLength={80}
              onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="pf-city">所在城市</label>
            <input
              id="pf-city"
              className="field-input"
              value={form.city}
              maxLength={20}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            />
          </div>
          {form.fullName.trim().length === 0 && <div className="field-error">姓名不能为空。</div>}
          <Button type="submit" variant="primary" block disabled={!canSubmit}>
            {saveProfile.isPending ? '正在保存…' : '保存修改'}
          </Button>
          {!dirty && !saveProfile.isPending && <div className="field-hint">当前没有未保存的修改。</div>}
        </form>
      </Card>

      {/* ---------------- 关于与合规 ---------------- */}
      <Card title="关于与合规">
        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <Avatar char={me.avatarChar} gold={me.level === 'founding'} />
          <div className="grow">
            <div className="li-t">{me.company}</div>
            <div className="li-s">
              {me.role} · {me.city}
            </div>
          </div>
        </div>
        <div className="divider" />
        <div className="text-tiny text-muted" style={{ lineHeight: 1.75 }}>
          专属管家：{me.guardian.name}（{me.guardian.title}）· {me.guardian.slaText}
          <br />
          服务督导：{me.adopter.name}（{me.adopter.title}）
        </div>
        <div className="note-src">
          <b>平台边界</b>：{complianceText.boundary}
          <br />
          <b>AI 口径</b>：{complianceText.disclaimer}
        </div>
        <div className="sec-note">
          平台以内部编号 + 职位称呼关键人，任何榜单、案例与宣传材料均不出现成员身份与个体数据。
        </div>
      </Card>

      {/* ---------------- 退出登录 ---------------- */}
      <Card title="账号与登录">
        {confirmLogout ? (
          <div className="stack">
            <div className="text-tiny text-muted">
              退出后将吊销服务端会话，需重新登录才能查看健康、企业与财富数据。确认退出？
            </div>
            <div className="row-flex">
              <Button variant="danger" small disabled={doLogout.isPending} onClick={() => doLogout.mutate()}>
                {doLogout.isPending ? '正在退出…' : '确认退出登录'}
              </Button>
              <Button small onClick={() => setConfirmLogout(false)}>
                取消
              </Button>
            </div>
          </div>
        ) : (
          <Button block onClick={() => setConfirmLogout(true)}>
            退出登录
          </Button>
        )}
      </Card>

      <div className="sec-note text-center">
        四级数据授权 · 可随时撤回 · 每次调用全量留痕
        <br />
        {me.levelEn} · {me.memberNo}
      </div>
    </div>
  );
}
